// src/widget/search/entry.jsx
import React from "react";
import { createRoot } from "react-dom/client";
import Widget from "./Widget.jsx";
import { logger } from "../shared/logger";

// Store roots for reload functionality
const widgetRoots = new Map();

(function () {
  // IMMEDIATELY inject skeleton loader CSS and show loading state
  // This prevents flash of unstyled content (FOUC) and shows a placeholder
  (function injectSkeletonCSS() {
    if (document.getElementById('nexus-search-skeleton-css')) return;
    const style = document.createElement('style');
    style.id = 'nexus-search-skeleton-css';
    style.textContent = `
      /* Hide actual widget content until ready */
      #nexus-search-widget > .nexus-search-widget,
      .nexus-search-widget > .nexus-search-widget,
      [data-nexus-search] > .nexus-search-widget {
        visibility: hidden !important;
        opacity: 0 !important;
      }
      #nexus-search-widget.nexus-styles-ready > .nexus-search-widget,
      .nexus-search-widget.nexus-styles-ready > .nexus-search-widget,
      [data-nexus-search].nexus-styles-ready > .nexus-search-widget {
        visibility: visible !important;
        opacity: 1 !important;
        transition: opacity 0.2s ease-out !important;
      }

      /* Skeleton loader styles */
      .nexus-search-skeleton {
        display: flex;
        gap: 8px;
        padding: 12px;
        border-radius: 12px;
        border: 1px solid rgba(15,23,42,.15);
        background: #f8fafc;
        align-items: center;
      }
      .nexus-search-skeleton-pill {
        flex: 2;
        height: 44px;
        border-radius: 10px;
        background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
        background-size: 200% 100%;
        animation: nexus-skeleton-shimmer 1.5s infinite;
      }
      .nexus-search-skeleton-btn {
        flex: 1;
        height: 44px;
        border-radius: 10px;
        background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
        background-size: 200% 100%;
        animation: nexus-skeleton-shimmer 1.5s infinite;
      }
      .nexus-search-skeleton-search {
        flex: 0 0 auto;
        width: 90px;
        height: 44px;
        border-radius: 10px;
        background: linear-gradient(90deg, #cbd5e1 25%, #e2e8f0 50%, #cbd5e1 75%);
        background-size: 200% 100%;
        animation: nexus-skeleton-shimmer 1.5s infinite;
      }
      @keyframes nexus-skeleton-shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }

      /* Hide skeleton when widget is ready */
      .nexus-styles-ready .nexus-search-skeleton {
        display: none !important;
      }
    `;
    // Insert at the very beginning of head for highest priority
    if (document.head.firstChild) {
      document.head.insertBefore(style, document.head.firstChild);
    } else {
      document.head.appendChild(style);
    }
  })();

  // Inject skeleton loader into mount elements immediately
  function injectSkeleton(el) {
    if (el.querySelector('.nexus-search-skeleton')) return; // Already has skeleton
    const skeleton = document.createElement('div');
    skeleton.className = 'nexus-search-skeleton';
    skeleton.innerHTML = `
      <div class="nexus-search-skeleton-pill"></div>
      <div class="nexus-search-skeleton-pill"></div>
      <div class="nexus-search-skeleton-pill"></div>
      <div class="nexus-search-skeleton-btn"></div>
      <div class="nexus-search-skeleton-search"></div>
    `;
    el.insertBefore(skeleton, el.firstChild);
  }

  // Add preconnect hints for faster API connections
  function addPreconnectHints() {
    const apiDomain = "https://api.nexus.design";

    // Check if preconnect already exists
    if (document.querySelector(`link[rel="preconnect"][href="${apiDomain}"]`)) {
      return;
    }

    // Add preconnect
    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = apiDomain;
    document.head.appendChild(preconnect);

    // Add dns-prefetch as fallback
    const dnsPrefetch = document.createElement("link");
    dnsPrefetch.rel = "dns-prefetch";
    dnsPrefetch.href = apiDomain;
    document.head.appendChild(dnsPrefetch);
  }

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  // Add preconnect hints immediately (before DOM ready)
  addPreconnectHints();

  ready(() => {
    const nodes = document.querySelectorAll(
      "#nexus-search-widget, .nexus-search-widget, [data-nexus-search]"
    );
    if (!nodes.length) {
      logger.warn("[nexus-search] mount element not found");
      return;
    }

    nodes.forEach((el) => {
      // 🔹 Inject skeleton loader FIRST (before React renders)
      injectSkeleton(el);

      // 🔹 read config from data-attrs (keep this tiny & consistent)
      const theme = (el.getAttribute("data-theme") || "light").toLowerCase();
      const maxGuests = parseInt(el.getAttribute("data-max-guests") || "16", 10);
      const apiOrigin = el.getAttribute("data-api-origin") || "https://api.nexus.design";

      // account can be id or account attr (same pattern as grid)
      const account =
        el.getAttribute("data-account-id") ||
        el.getAttribute("data-account") ||
        "";

      // You can pass more attrs here later if needed

      // Check if already mounted
      if (widgetRoots.has(el)) {
        const existingRoot = widgetRoots.get(el);
        existingRoot.render(
          <Widget
            theme={theme}
            maxGuests={maxGuests}
            apiOrigin={apiOrigin}
            accountIdProp={account}
            key={Date.now()} // Force re-render
          />
        );
        return;
      }

      const root = createRoot(el);
      widgetRoots.set(el, root);
      root.render(
        <Widget
          theme={theme}
          maxGuests={maxGuests}
          apiOrigin={apiOrigin}
          // Widget reads account from URL or host el, but we can pass it down too
          accountIdProp={account}
        />
      );
    });
  });

  // Expose reload function globally
  window.NexusSearchReload = function() {
    const nodes = document.querySelectorAll(
      "#nexus-search-widget, .nexus-search-widget, [data-nexus-search]"
    );
    nodes.forEach((el) => {
      const theme = (el.getAttribute("data-theme") || "light").toLowerCase();
      const maxGuests = parseInt(el.getAttribute("data-max-guests") || "16", 10);
      const apiOrigin = el.getAttribute("data-api-origin") || "https://api.nexus.design";
      const account = el.getAttribute("data-account-id") || el.getAttribute("data-account") || "";

      if (widgetRoots.has(el)) {
        const root = widgetRoots.get(el);
        root.render(
          <Widget
            theme={theme}
            maxGuests={maxGuests}
            apiOrigin={apiOrigin}
            accountIdProp={account}
            key={Date.now()} // Force re-render with new data attributes
          />
        );
      }
    });
  };
})();
